import React from 'react';
import { Course, CourseProgress } from '../types';
import { useLanguage } from '../context/LanguageContext';
import {
  CheckCircle,
  Lock,
  Play,
  Award,
  Sparkles,
  Shield,
  HelpCircle,
  Zap,
  Flame
} from 'lucide-react';

interface SkillTreeMapProps {
  course: Course;
  progress: CourseProgress;
  activeTab: 'lessons' | 'game' | 'exam';
  onSelectTab: (tab: 'lessons' | 'game' | 'exam') => void;
}

export const SkillTreeMap: React.FC<SkillTreeMapProps> = ({
  course,
  progress,
  activeTab,
  onSelectTab,
}) => {
  const { t } = useLanguage();

  const completedModulesCount = (progress.moduleProgress || []).filter((m) => m.completed).length;
  const isModulesCompleted = completedModulesCount >= 3;
  const isScenarioCompleted = !!(progress.branchingScenarioCompleted || progress.miniGameCompleted);
  const isExamPassed = !!progress.examPassed;

  const nodes = [
    {
      id: 'lessons',
      stageNumber: 1,
      title: t('Fase 1: Fundamentos Normativos', 'Stage 1: Core Regulations'),
      subtitle: `${completedModulesCount}/3 ${t('módulos superados', 'modules completed')}`,
      isUnlocked: true,
      isCompleted: isModulesCompleted,
      isActive: activeTab === 'lessons',
      tabTarget: 'lessons' as const,
      color: '#3b82f6',
      icon: <Play size={18} />,
    },
    {
      id: 'game',
      stageNumber: 2,
      title: t('Fase 2: Simulador de Crisis & Ramificaciones', 'Stage 2: Branching Crisis Simulator'),
      subtitle: isScenarioCompleted ? t('Dilema resuelto (+20% XP)', 'Dilemma solved (+20% XP)') : t('Simulación de alto riesgo', 'High-stakes simulation'),
      isUnlocked: true, // Allow learner autonomy while showing recommended progression
      isCompleted: isScenarioCompleted,
      isActive: activeTab === 'game',
      tabTarget: 'game' as const,
      color: '#f59e0b',
      icon: <Zap size={18} />,
    },
    {
      id: 'exam',
      stageNumber: 3,
      title: t('Fase 3: Evaluación Oficial (80% Passing)', 'Stage 3: Official Assessment (80% Passing)'),
      subtitle: isExamPassed ? t('Aprobado con éxito', 'Passed successfully') : t('12 preguntas aleatorias', '12 random questions'),
      isUnlocked: isModulesCompleted || isScenarioCompleted,
      isCompleted: isExamPassed,
      isActive: activeTab === 'exam',
      tabTarget: 'exam' as const,
      color: '#10b981',
      icon: <Award size={18} />,
    },
    {
      id: 'badge',
      stageNumber: 4,
      title: t('Fase 4: Credencial Open Badges 3.0', 'Stage 4: Open Badges 3.0 Credential'),
      subtitle: isExamPassed ? t('Emitido y Sellado', 'Issued & Verified') : t('Bloqueado hasta certificar', 'Locked until certified'),
      isUnlocked: isExamPassed,
      isCompleted: isExamPassed,
      isActive: false,
      tabTarget: 'exam' as const,
      color: '#8b5cf6',
      icon: <Shield size={18} />,
    },
  ];

  return (
    <div
      className="glass-card"
      style={{
        padding: '20px 24px',
        borderRadius: 'var(--radius-lg)',
        marginBottom: '24px',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(10, 15, 29, 0.9) 100%)',
        border: '1px solid var(--border-color)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Sparkles size={16} color="#fbbf24" />
          <span style={{ fontSize: '12px', fontWeight: 800, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
            {t('Mapa de Progresión & Desbloqueo de Habilidades (Escasez)', 'Skill Progression & Scarcity Tree Map')}
          </span>
        </div>
        <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>
          {t('Nivel de Maestría:', 'Mastery Level:')}{' '}
          <strong style={{ color: isExamPassed ? '#34d399' : '#60a5fa' }}>
            {isExamPassed ? t('100% Certificado', '100% Certified') : `${progress.progressPercent}%`}
          </strong>
        </div>
      </div>

      {/* Interactive Node Graph */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px',
          position: 'relative',
        }}
      >
        {nodes.map((node, index) => {
          return (
            <div
              key={node.id}
              onClick={() => {
                if (node.isUnlocked && node.tabTarget) {
                  onSelectTab(node.tabTarget);
                }
              }}
              style={{
                padding: '14px',
                borderRadius: 'var(--radius-md)',
                background: node.isActive
                  ? 'rgba(59, 130, 246, 0.15)'
                  : node.isCompleted
                  ? 'rgba(16, 185, 129, 0.08)'
                  : 'var(--bg-secondary)',
                border: `1px solid ${
                  node.isActive
                    ? '#3b82f6'
                    : node.isCompleted
                    ? 'rgba(16, 185, 129, 0.3)'
                    : 'var(--border-color)'
                }`,
                cursor: node.isUnlocked ? 'pointer' : 'not-allowed',
                opacity: node.isUnlocked ? 1 : 0.55,
                transition: 'all 0.2s ease',
                position: 'relative',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: node.isCompleted ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255,255,255,0.06)',
                    color: node.isCompleted ? '#34d399' : node.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {node.isCompleted ? <CheckCircle size={18} /> : node.isUnlocked ? node.icon : <Lock size={16} />}
                </div>

                <span
                  style={{
                    fontSize: '10.5px',
                    fontWeight: 800,
                    color: node.isCompleted ? '#34d399' : 'var(--text-muted)',
                    textTransform: 'uppercase',
                  }}
                >
                  {node.isCompleted ? t('Completado', 'Done') : !node.isUnlocked ? t('Bloqueado', 'Locked') : t('Disponible', 'Available')}
                </span>
              </div>

              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '3px' }}>
                {node.title}
              </div>

              <div style={{ fontSize: '11.5px', color: 'var(--text-secondary)' }}>
                {node.subtitle}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
